import React, {useEffect, useMemo, useRef, useState} from "react";
import {LoadingMessage, LoadingPanel, PageContainer, Separator, Time, CourseTitle, ZoomWindow, Hint, } from "./LectureRoomPage.style";
import {LoadingIndicator} from "../../toolympus/components/primitives/LoadingIndicator";
import {TutorInfo, TutorName} from "../AboutCourse/presentation/AboutCoursePage.style";
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import VideocamIcon from '@mui/icons-material/Videocam';
import moment from "moment";
import {Button} from "../common/Button";
import { Jutsu } from 'react-jutsu'
import { useTheme } from '@mui/material';
import { ZoomData } from "./types";
import { Schedule } from "../CourseManagement/types";
import { participantName, tutorPosition } from "../Participants/presentation/display";
import { useUser } from "../../toolympus/userContext/UserContext";
import { LectureRoomPlatformOptions, useSettings } from "../settings/useSettingsEdit";
import { CMSRecord } from "../../toolympus/components/CMS";
import { useLoadedData } from "../../toolympus/hooks/useLoadedData";
import { CustomElement } from "../../slate";
import { CmsTextViewer } from "../Application/ApplicationForm";

const TB_FOR_MODERATOR = [
    'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
    'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
    'livestreaming', 'sharedvideo', 'settings', 'raisehand',
    'videoquality', 'filmstrip', 'feedback', 'stats', 'shortcuts',
    'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone'
];


const TB_FOR_NO_MODERATOR = [
    'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
    'fodeviceselection', 'hangup', 'chat',
    'sharedvideo', 'settings', 'raisehand',
    'videoquality', 'filmstrip', 'feedback', 'stats', 'shortcuts',
    'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone'
];

interface Props {
    jitsi: {
        data: ZoomData | null, 
        loading: boolean
    },
    subject: {
        schedule?: Schedule,
        loading: boolean
    }
}

const useJitsiScript = (jitsiDomain?: string) => {
    useEffect(() => {
        if(jitsiDomain) {
            const script = document.createElement('script');
          
            script.src = `https://${jitsiDomain}/external_api.js`;
          
            document.body.appendChild(script);
          
            return () => {
              document.body.removeChild(script);
            }
        }
      }, [jitsiDomain]);
}

const useLectureRoomTexts = () => {
  const data = useLoadedData<CMSRecord>(`/api/cms/public/public_docs/lecture_room`, { _id: "", } as any);

  const [jitsiContent,externalContent] = useMemo(() => {
    const jitsiPanel = (data.data.content as any)?.blocks?.find((b: any) => b.lecture_room_mode === LectureRoomPlatformOptions.jitsi);
    const externalPanel = (data.data.content as any)?.blocks?.find((b: any) => b.lecture_room_mode === LectureRoomPlatformOptions.external);

    return [
      (jitsiPanel ? jitsiPanel.content : { blocks: []}) as { blocks: CustomElement[] },
      (externalPanel ? externalPanel.content : { blocks: []}) as { blocks: CustomElement[] },
    ]
  }, [data.data]);


  return {
    contentKey: data.data.slug || "x",
    jitsiContent,
    externalContent,
    isLoading: data.isLoading,
  }
}

export const LectureRoomPage = ({jitsi, subject}: Props) => {
    const [openRoom, setOpenRoom] = useState<string | null>(null);
    const pageRef = useRef<HTMLElement>(null);
    const theme = useTheme();
    const settings = useSettings();

    useJitsiScript(jitsi.data?.available ? jitsi.data.domain : undefined);
    const { user } = useUser();

    const texts = useLectureRoomTexts();

    const hasAccessToPrivateRoom = (user?.roles || []).includes("tutor") || (user?.roles || []).includes("organizer") || (user?.roles || []).includes("admin");

    if (subject.loading || settings.isLoading || jitsi.loading || !jitsi.data) {
        return <LoadingIndicator />
    }

    const { tutors, start_datetime, end_datetime} = subject.schedule || { };

    const {room, name, available, token, domain, moderator} = jitsi.data;
    
    const TOOLBAR_BUTTONS = moderator? TB_FOR_MODERATOR : TB_FOR_NO_MODERATOR;


    const openWindow = (roomKind: "normal" | "private" = "normal") => {
      setOpenRoom(roomKind === "private" ? `[private] ${room}` : room);
      const page = (pageRef.current as HTMLElement).parentElement as HTMLElement;

      let count = 0;

      const cardHeight = page.scrollHeight / 15;

      const loopAnimation = () => {
          count++;
          page.scrollTo(0, count * cardHeight);

          if (count < 15) {
              setTimeout(loopAnimation, 20);
          }
      }

      loopAnimation();
    }

    const nButtons = 1 + (subject?.schedule?.external_link ? 2 : 0) + (hasAccessToPrivateRoom ? 1 : 0);

    return <PageContainer ref={pageRef as any}>
        {subject.schedule && <>
            <CourseTitle to={`/public/courses/${subject.schedule.course_id}`}>{subject.schedule.course?.title || ""}</CourseTitle>
            {(tutors || []).map(t => (
              <TutorInfo key={t._id}><TutorName>{participantName(t)},</TutorName> {tutorPosition(t)}</TutorInfo>
            ))}
            <Separator />
            <Time>
                <WatchLaterIcon color="disabled" />
                {moment(start_datetime).format("dddd, D MMMM: H:mm")} – {moment(end_datetime).format("H:mm")}
            </Time>
        </>}

        {(available || settings.data.lecture_room_platform === settings.lectureRoomOptions.external) &&
            <ZoomWindow open={!!openRoom}>
              <div className="buttons">
                {subject.schedule?.external_link && settings.data.lecture_room_platform === settings.lectureRoomOptions.jitsi &&
                  <Hint>This lecture takes place outside the platform</Hint>}
                {subject.schedule?.external_link &&
                  <Button
                    href={subject.schedule?.external_link}
                    target="_blank"
                    rel="noreferrer noopener"
                    variant="contained"
                    color="primary"
                    >
                      Join the lecture (external)
                  </Button>}

                {settings.data.lecture_room_platform === settings.lectureRoomOptions.jitsi && <>
                  <Button 
                      onClick={() => openWindow()}
                      variant={subject.schedule?.external_link ? "text" : "contained"}
                      color="primary" 
                      endIcon={<VideocamIcon/>}
                      >
                      {subject.schedule?.external_link ? "Join the lecture room" : "Join the lecture"}
                  </Button>

                  {hasAccessToPrivateRoom &&
                    <Button 
                      onClick={() => openWindow("private")}
                      color="primary" 
                      endIcon={<VideocamIcon/>}
                      >
                      Join the private room
                  </Button>}
                </>}

                {settings.data.lecture_room_platform === settings.lectureRoomOptions.external &&  <>
                  {!!settings.data.external_lecture_room_common_url && !subject.schedule?.external_link &&
                    <Button 
                      variant={subject.schedule?.external_link ? "text" : "contained"}
                      color="primary" 
                      endIcon={<VideocamIcon/>}
                      href={settings.data.external_lecture_room_common_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      >
                      {subject.schedule?.external_link ? "Join the lecture room" : "Join the lecture"}
                    </Button>}

                  {hasAccessToPrivateRoom && !!settings.data.external_lecture_room_private_url &&
                    <Button 
                      color="primary" 
                      endIcon={<VideocamIcon/>}
                      href={settings.data.external_lecture_room_private_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      >
                      Join the private room
                  </Button>}
                </>}

              </div>
                
                <LoadingPanel open={!!openRoom}>
                    <LoadingMessage>The lecture room is loading...</LoadingMessage>
                    <LoadingIndicator />
                </LoadingPanel>
                
                {!!openRoom && 
                    <Jutsu
                        loadingComponent={<>The lecture room is loading...</>}
                        containerStyles={{
                            width: '100%',
                            height: '100%',
                            border: '3px solid ' + theme.palette.primary.main,
                            position: 'absolute',
                        }}

                        roomName={openRoom}
                        displayName={name}
                        domain={domain}
                        jwt={token}
                        onMeetingEnd={() => setOpenRoom(null)}
                        interfaceConfigOverwrite={{
                            HIDE_INVITE_MORE_HEADER: true,
                            TOOLBAR_BUTTONS
                        }}
                    />
                }
                
                <Hint offset={nButtons}></Hint>

                {settings.data.lecture_room_platform === settings.lectureRoomOptions.jitsi &&
                  <CmsTextViewer key={`${texts.contentKey}_jitsi`} content={texts.jitsiContent} />}

                {settings.data.lecture_room_platform === settings.lectureRoomOptions.jitsi && <>
                  <Hint><a href={`https://${domain}/${room}?jwt=${token}`} target="_blank" rel="noopener noreferrer">lecture room link</a></Hint>
                </>}

                {settings.data.lecture_room_platform === settings.lectureRoomOptions.external &&
                  <CmsTextViewer key={`${texts.contentKey}_external`} content={texts.externalContent} />}
            </ZoomWindow>
        }
    </PageContainer>
}
